import { Constructor, Mutable } from '@skylab-shared/types';
import { flat, head } from '@skylab-shared/utils';
import { Weaver } from '../../aop';
import { BusinessObjectBase } from './../../bo';
import { Domain, ModelBase, PropertyType } from './../../model';
import { ObservableCollection } from './../../observable';
import { AttachedRule } from './attached-rule';
import {
    Rule,
    RuleDefinition,
    RuleFunc,
    RuleResult,
    RulesEngine
} from './rules-engine';
import { mergeDefinitions } from './utils/merge-definitions';

export type ReadonlyRuleResult = Readonly<{ readonly: boolean; }>;

export class ReadonlyEngine<TBo extends BusinessObjectBase<TModel>, TModel extends ModelBase> extends RulesEngine<TBo, TModel, ReadonlyRuleResult> {
    public constructor(weaver: Weaver) {
        super(weaver);
    }

    public get readonlyProperties(): ObservableCollection<ReadonlyRuleResult & RuleResult> {
        return this.result;
    }

    public setAsReadonly<TModel extends ModelBase>(property: PropertyType<TModel>): void {
        this.addRule([property], _ => Promise.resolve(true));
    }

    public setReadonlyOnSubtree(
        modelType: Constructor<ModelBase>,
        func: RuleFunc<BusinessObjectBase<ModelBase>, ModelBase, ModelBase>,
        settings?: {
            exceptions?: PropertyType<any>[];
        }
    ): void {
        const exceptions = settings?.exceptions || [];

        const propertiesInDomainWithType = getPropertiesWithType(this.domain, modelType);
        const [[parentType, propertyName]] = propertiesInDomainWithType;
        const desendantProperties = Domain.getAllDescendantProperties([parentType, propertyName])
            .filter(([type, propertyName]) => !exceptions.some(([t, p]) => type === t && propertyName === p));

        const inSetOfModels = (model: ModelBase) => flat(this.domain
            .getModels(this.bo.model)
            .map(([model, _]) => model)
            .map(model => flat(propertiesInDomainWithType.map(([parentType, propertyName]) =>
                model instanceof parentType
                    ? (model.get(propertyName) instanceof ObservableCollection
                        ? model.get(propertyName).asArray()
                        : [model.get(propertyName)])
                    : null
            ))))
            .indexOf(model) > -1;

        desendantProperties.forEach(property => this.addRule(
            [property],
            async ctx => inSetOfModels(ctx.source) && await Promise.resolve(func(ctx))
        ));
    }

    protected override prepareAndAddRuleDefinition(
        triggeredBy: PropertyType<ModelBase>[],
        effectOn: PropertyType<ModelBase>[],
        rule: Rule,
        args: undefined
    ): void {
        effectOn.forEach(e => {
            const [target]: Mutable<RuleDefinition>[] = this.ruleDefinitions
                .filter(def => this.hasSamePrototype(def.effectOn[0][0], e[0]) && (def.effectOn[0][1] === e[1]));
            if (target) {
                const ruleDefinition = this.prepareRuleDefinition(triggeredBy, effectOn, rule, args);
                mergeDefinitions(target, ruleDefinition);
            } else {
                super.prepareAndAddRuleDefinition(triggeredBy, [e], rule, args);
            }
        });
    }

    private hasSamePrototype(c1: Constructor<ModelBase>, c2: Constructor<ModelBase>) {
        if (!c1 || !c2) return;
        return (c1 === c2) || (c1.prototype instanceof c2) || (c2.prototype instanceof c1);
    }

    protected createAttachedRule(ruleDefinietion: RuleDefinition, triggeredByModel: ModelBase, effectOnModel: ModelBase): AttachedRule<ReadonlyRuleResult> {
        return new AttachedReadonlyRule(ruleDefinietion, this.bo, triggeredByModel, effectOnModel);
    }

    public executeRuleForProperty<T extends ModelBase>(prop: keyof T, model: T): boolean {
        const definitions = this.ruleDefinitions.filter(elem => {
            const [modelType, propertyName] = head(elem.effectOn);
            return (propertyName === prop) && this.hasSamePrototype(modelType, model.constructor as Constructor<ModelBase>);
        });
        const rules = this.attachedRules.filter(elem => definitions.map(def => def.id).includes(elem.ruleId) && elem.attachedToTarget(model));
        return rules.reduce((p, c) => p || c.invokeSync()?.[0]?.readonly, false);
    }
}

export class AttachedReadonlyRule extends AttachedRule<ReadonlyRuleResult> {
    protected async validateRule(): Promise<ReadonlyRuleResult> {
        const ctx = { bo: this.bo, source: this.source, target: this.target };
        const isReadonly = await this.ruleDefinition.rule.funcAsync(ctx);
        return {
            readonly: isReadonly
        };
    }

    public override validateRuleSync(): ReadonlyRuleResult {
        const ctx = { bo: this.bo, source: this.source, target: this.target };
        const isReadonly = this.ruleDefinition.rule.func(ctx);
        return {
            readonly: isReadonly
        };
    }
}

//#region Helpers

const getPropertiesWithType = (domain: Domain, modelType: Constructor<ModelBase>) => {
    const modelTypeProperties = <PropertyType<ModelBase>[]>[];
    domain.traverseDomainNodes((node, parentNode) => {
        if (node.modelType === modelType && parentNode && node.property) {
            modelTypeProperties.push([parentNode.modelType, <keyof ModelBase>node.property.propertyName]);
        }
    });
    return modelTypeProperties;
};

//#endregion
